import { useEffect, useRef, useState } from "react";
import { Terminal } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { getToken, type Me, type ServerConfig } from "../lib/api.js";
import { Drawer, Notice } from "../components/ui.js";
import { RemoteSession } from "rustdesk-protocol/session.js";
import { RemoteTerminal } from "rustdesk-protocol/terminal.js";
import { savedHash } from "../lib/credentials.js";

/**
 * A shell on the remote machine.
 *
 * Runs on its own connection: RustDesk treats the terminal as a distinct
 * connection type, and the peer starts its terminal service on login rather
 * than multiplexing it into a screen session.
 *
 * xterm.js does the VT emulation, so escape sequences, colour and cursor
 * addressing behave as they would in a real terminal.
 */
export default function TerminalView({
  peerId,
  me,
  serverKeyProvider,
  password,
  onClose,
}: {
  peerId: string;
  me: Me;
  serverKeyProvider: () => Promise<ServerConfig>;
  password?: string;
  onClose: () => void;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const termRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const sessionRef = useRef<RemoteSession | null>(null);
  const remoteRef = useRef<RemoteTerminal | null>(null);

  const [status, setStatus] = useState("Connecting…");
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    if (!host) return;

    const term = new Terminal({
      fontFamily: '"IBM Plex Mono", ui-monospace, Menlo, Consolas, monospace',
      fontSize: 13,
      cursorBlink: true,
      convertEol: false,
      theme: {
        background: "#12151a",
        foreground: "#dfe4ec",
        cursor: "#37cfb4",
        selectionBackground: "#2b323d",
      },
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(host);
    fit.fit();
    termRef.current = term;
    fitRef.current = fit;

    (async () => {
      try {
        const cfg = await serverKeyProvider();
        // A terminal is its own connection, so it needs its own credentials.
        // The screen session may have been authorised by a saved hash or by
        // someone accepting at the machine, in which case there is no password
        // here to reuse - fall back to the saved hash, as the desktop client
        // reuses the credentials it already has.
        const hash = password ? null : await savedHash(peerId);
        const session = new RemoteSession(
          {
            origin: window.location.origin,
            token: getToken(),
            peerId,
            serverKey: cfg.key,
            password,
            passwordHash: hash ?? undefined,
            connType: "terminal",
            myName: me.display_name || me.name,
            myId: "web",
          },
          {
            onState: (s) => {
              if (s === "connected") setStatus("Connected");
              else if (s === "authenticating") {
                setStatus(
                  password || hash
                    ? "Signing in"
                    : "Waiting for someone at the machine to accept",
                );
              }
            },
            onPasswordRequired: (wrong) =>
              setError(
                wrong
                  ? "The saved password for this machine was not accepted. Reconnect the session and enter it again."
                  : "This machine needs a password. Reconnect the session and enter it.",
              ),
            onError: (m) => setError(m),
            onClose: (r) => setStatus(r || "Disconnected"),
            onPeerInfo: () => {
              if (cancelled) return;
              // The peer's terminal service is up once login completes.
              // fit() before layout can report 0; the peer rejects that grid.
              const rows = term.rows > 0 ? term.rows : 24;
              const cols = term.cols > 0 ? term.cols : 80;
              remoteRef.current?.open(0, rows, cols);
            },
            onMessage: (m) => remoteRef.current?.handle(m),
          },
        );
        sessionRef.current = session;

        remoteRef.current = new RemoteTerminal(session, {
          onOpened: () => {
            if (cancelled) return;
            setReady(true);
            setStatus("Connected");
            term.focus();
          },
          onOutput: (_id, data) => term.write(remoteRef.current!.decode(data)),
          onClosed: (_id, code) => {
            term.writeln(`\r\n\x1b[90m[session ended, exit code ${code}]\x1b[0m`);
            setReady(false);
          },
          onError: (m) => setError(m),
        });

        // xterm gives us the typed data already encoded for a PTY.
        term.onData((data) => remoteRef.current?.write(data));

        await session.connect();
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();

    const onResize = () => {
      fitRef.current?.fit();
      const t = termRef.current;
      if (t) remoteRef.current?.resize(t.rows, t.cols);
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      remoteRef.current?.close(0);
      sessionRef.current?.close("terminal closed");
      sessionRef.current = null;
      remoteRef.current = null;
      term.dispose();
      termRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the remote PTY the same size as the view.
  useEffect(() => {
    if (!ready) return;
    const t = termRef.current;
    if (!t) return;
    fitRef.current?.fit();
    remoteRef.current?.resize(t.rows, t.cols);
  }, [ready]);

  return (
    <Drawer title={`Terminal on ${peerId}`} onClose={onClose}>
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}
        {!ready && !error ? <p className="faint">{status}…</p> : null}
        <div
          ref={hostRef}
          style={{
            height: "60vh",
            minHeight: 320,
            background: "#12151a",
            border: "1px solid var(--line)",
            borderRadius: "var(--radius)",
            padding: "0.5rem",
          }}
        />
        <p className="small faint" style={{ margin: 0 }}>
          Runs as the account the RustDesk service uses on that machine.
        </p>
      </div>
    </Drawer>
  );
}
